import {
  Box,
  Checkbox,
  HStack,
  Icon,
  IconName,
  Skeleton,
  Text,
  Tooltip,
  useBoolean,
  VStack,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LouiseOffer } from '../../../../../../../../types/Quotes';
import convertKeyToIconName from '../../../../../../../common/src/utils/convertKeyToIconName';
import { toEuro } from '../../../../../utils/stringUtils';
import useOffer from '../../../store/offer/useOffer';
import useProductContent from '../../../store/offer/useProductContent';
import useQuoteGuarantees from '../../../store/offer/useQuoteGuarantees';

const OfferOptions: React.FC = () => {
  const { t } = useTranslation();
  const { offer, isLoading } = useOffer();
  const { selectedGuarantees, setSelectedGuarantees } = useQuoteGuarantees();
  const productContent = useProductContent();

  const handleGuaranteesChange = (guarantee: string) => {
    if (selectedGuarantees?.includes(guarantee)) {
      setSelectedGuarantees(selectedGuarantees.filter((g) => g !== guarantee));
    } else {
      setSelectedGuarantees([...selectedGuarantees, guarantee]);
    }
  };

  return (
    <VStack align="stretch">
      <Text color="gray.500">
        {offer.possible_guarantees.length > 1 ? t('COVERAGES') : t('COVERAGE')}
      </Text>
      <VStack align="stretch">
        {offer.possible_guarantees?.map((guarantee, index) => (
          <OfferSingleOption
            key={guarantee}
            guarantee={guarantee}
            isEven={index % 2 === 0}
            isLoading={isLoading}
            isSelected={selectedGuarantees.includes(guarantee)}
            onChange={() => handleGuaranteesChange(guarantee)}
            offer={offer}
            isDisabled={
              isLoading ||
              productContent?.mandatory_guarantees?.includes(guarantee) ||
              offer.guarantees?.find(({ name }) => name === guarantee)?.mandatory
            }
          />
        ))}
      </VStack>
    </VStack>
  );
};

interface CoverageOptionProps {
  offer: LouiseOffer;
  guarantee: string;
  isEven: boolean;
  isLoading?: boolean;
  isSelected: boolean;
  isDisabled?: boolean;
  onChange: () => void;
}

const OfferSingleOption: React.FC<CoverageOptionProps> = ({
  offer,
  guarantee,
  isEven,
  isLoading,
  isSelected,
  isDisabled,
  onChange,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useBoolean();
  const productContent = useProductContent();
  const insuranceType = productContent?.insurance_type;

  const guaranteeContent = productContent?.guarantee_content?.find(
    ({ key }) => key === guarantee,
  );

  const guaranteeName = guaranteeContent?.name || guarantee;
  const guaranteeDescription = productContent?.guarantee_content?.find(
    ({ key }) => key === guarantee,
  )?.description;

  const guaranteePremium = offer.guarantees?.find(
    ({ name }) => name === guarantee,
  )?.total_premium;

  return (
    <Checkbox
      bg={isEven ? 'transparent' : 'gray.ultraLight'}
      w="100%"
      sx={{ 'span:last-child': { width: '100%' } }}
      p="1.5"
      borderRadius="sm"
      isChecked={isSelected}
      onChange={onChange}
      isDisabled={isDisabled}
      _disabled={{
        span: {
          opacity: 1,
        },
      }}
    >
      <HStack w="100%">
        <HStack flexGrow={1} color="gray.500">
          <Icon
            name={
              insuranceType
                ? convertKeyToIconName(guarantee, insuranceType)
                : (guarantee as IconName)
            }
            // Extra color prop required here for legacy icons
            color="gray.500"
          />
          <Text textStyle="smallBody">{guaranteeName}</Text>

          {guaranteeDescription && (
            <Tooltip
              onClick={(e) => {
                e.preventDefault();
                setIsTooltipOpen.off();
              }}
              closeOnClick
              label={guaranteeDescription}
              isOpen={isTooltipOpen || undefined}
            >
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  setIsTooltipOpen.toggle();
                }}
              >
                <Icon name="GeneralStatusInfoOutline" boxSize={4} />
              </Box>
            </Tooltip>
          )}
        </HStack>
        {isLoading ? (
          <Skeleton w="14" h="5" />
        ) : (
          <Text textStyle="smallBody">
            {guaranteePremium != null
              ? toEuro(guaranteePremium, offer.premium?.currency)
              : '-'}
          </Text>
        )}
      </HStack>
    </Checkbox>
  );
};

export default OfferOptions;
